import {useClaims} from "Hooks/api/useClaims";
import {useMemo, useState} from "react";
import {PROMO_PREFIXES} from "Constants";
import extendedDayjs from "Helpers/dayjs";
import {_useUnclaimedPromotions} from "Hooks/api/usePromotions";
import {
    GAMBIT_DOUBLE_WHEEL_MODAL_ID,
    GAMBIT_MATCH_THREE_MODAL_ID,
    GAMBIT_PENALTY_SHOOTOUT_MODAL_ID,
    GAMBIT_SINGLE_WHEEL_MODAL_ID,
    IS_DEV,
    IS_DOUBLE_WHEEL_ENABLED,
    IS_MATCH_THREE_ENABLED,
    IS_PENALTY_SHOOTOUT_ENABLED, IS_QA3,
    IS_SINGLE_WHEEL_ENABLED,
    SHARED_CDN_PATH
} from "Setup/config";
import {sleep} from "Helpers/async";
import useGames from "Hooks/useGames";
import {makeStyles} from "@material-ui/core";
import {sendLogglyEvent} from "Helpers/loggly";
import {sendGA4Event} from "Helpers/gaEvents";
// import {activeTheme} from "Redux/reducers/uiReducer";
// import {useGomTheme} from "Hooks/ui/useGomTheme";

export const MATCH_THREE_CARDS = "MatchThreeCards";
export const DOUBLE_WHEEL = "DoubleWheel";
export const SINGLE_WHEEL = "SingleWheel";
export const PENALTY_SHOOTOUT = "PenaltyShootout";

const GAMIFICATION_ASSETS_PATH = `${SHARED_CDN_PATH}/Gamification/%GAME%/`;
const GAMIFICATION_IMAGE_PATH = `${GAMIFICATION_ASSETS_PATH}webp/`;

const setAssetsPathForGamification = (game) => GAMIFICATION_ASSETS_PATH.replace('%GAME%', game);
const setImagesPathForGamification = (game) => GAMIFICATION_IMAGE_PATH.replace('%GAME%', game);

const gambitGameConfigSkins = {
    [MATCH_THREE_CARDS]: "SantasWorkshop",
    [DOUBLE_WHEEL]: "DoubleWheel",
    [SINGLE_WHEEL]: "SingleWheel",
    [PENALTY_SHOOTOUT]: "PenaltyShootout",
};

const useDefaultStyles = makeStyles((theme) => {
    const settings = theme.overrides.DailyDealV2Modal;

    return {
        root: {
            display: "flex",
            flexDirection: "column",
            maxWidth: "100%",
            margin: "0 auto",
            height: '100%',
            position: "relative",
            backgroundSize: "cover",
            backgroundPosition: "center top",
            minHeight: '600px',

            [theme.breakpoints.up('sm')]: {
                height: `min(90vh, 720px)`,
            }
        },
        paper: {
            maxWidth: 1080,
            color: settings.textColor,
            backgroundSize: "cover",
            border: `10px solid ${settings.borderColor}`,

            '& .bg--bonus-money-strapline': {
                color: "white"
            }
        }
    }
});

const usePenaltyShootoutGameSpecificStyles = makeStyles((theme) => ({
    root: {
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundColor: "black",
        backgroundRepeat: "no-repeat",

        [theme.breakpoints.down(640)]: {
            minHeight: "unset",
            maxHeight: "100%",
            backgroundSize: "215% 95%",
            backgroundPosition: "center -20px",
        },
    },

    paper: {
        containerName: "gambitGame",
        containerType: "inline",
        [theme.breakpoints.down(640)]: {
            height: "min(90vh, 667px)",
            width: "min(90vw, 375px)",
        }
    }
}), {index: 10});

const useSingleWheelGameSpecificStyles = makeStyles((theme) => ({
    root: {
        backgroundPosition: "center bottom",
    },
}), {index: 10});

const getGambitGameConfig = (configId) => {
    const defaultSettings = {
        id: configId,
        audioEnabled: false,
        videoBackgroundEnabled: false,
        setGameClaimOnPlay: true,
        gameClaimName: configId,
        desktopBackground: 'desktop',
        mobileBackground: 'mobile',
        useDefaultStyles,
        useGameSpecificStyles: () => {
        },
    };

    const configs = {
        [MATCH_THREE_CARDS]: {
            enabled: IS_MATCH_THREE_ENABLED,
            audioEnabled: true,
            mastergroupBonus: PROMO_PREFIXES.GAMBIT_MATCH_THREE_GAME_MASTERGROUP,
            prizeBonus: PROMO_PREFIXES.GAMBIT_MATCH_THREE_GAME_PRIZE,
            prizeSymbols: ["❄️", "🎄", "🎅"],
            modalId: GAMBIT_MATCH_THREE_MODAL_ID
        },
        [DOUBLE_WHEEL]: {
            enabled: IS_DOUBLE_WHEEL_ENABLED,
            mastergroupBonus: PROMO_PREFIXES.GAMBIT_DOUBLE_WHEEL_GAME_MASTERGROUP,
            prizeBonus: PROMO_PREFIXES.GAMBIT_DOUBLE_WHEEL_GAME_PRIZE,
            modalId: GAMBIT_DOUBLE_WHEEL_MODAL_ID
        },
        [SINGLE_WHEEL]: {
            enabled: IS_SINGLE_WHEEL_ENABLED,
            mastergroupBonus: PROMO_PREFIXES.GAMBIT_SINGLE_WHEEL_GAME_MASTERGROUP,
            prizeBonus: PROMO_PREFIXES.GAMBIT_SINGLE_WHEEL_GAME_PRIZE,
            modalId: GAMBIT_SINGLE_WHEEL_MODAL_ID,
            useGameSpecificStyles: useSingleWheelGameSpecificStyles,
            variants: {
                "CaptainCodWheel": {
                    audioEnabled: true,
                    videoBackgroundEnabled: true
                },
                "WoollySafariWheel": {},
            }
        },
        [PENALTY_SHOOTOUT]: {
            enabled: IS_PENALTY_SHOOTOUT_ENABLED,
            audioEnabled: true,
            mastergroupBonus: PROMO_PREFIXES.GAMBIT_PENALTY_SHOOTOUT_GAME_MASTERGROUP,
            prizeBonus: PROMO_PREFIXES.GAMBIT_PENALTY_SHOOTOUT_GAME_PRIZE,
            modalId: GAMBIT_PENALTY_SHOOTOUT_MODAL_ID,
            mobileBackground: 'desktop',
            useGameSpecificStyles: usePenaltyShootoutGameSpecificStyles,
        },
    }

    const chosenConfig = configs?.[configId];

    if (chosenConfig) {
        return {
            ...defaultSettings,
            ...chosenConfig
        };
    }

    return null;
}

export const useGambitGameV2 = (game = null) => {
    const isTestMode = IS_DEV || IS_QA3;

    // const {appliedTheme} = useGomTheme();
    const TODAY = extendedDayjs().format("DD-MM-YYYY");
    const GAMBIT_GAMES_CLAIM = "GambitGamesState";
    const GAME_CONFIG = getGambitGameConfig(game);
    const GAME_CLAIM_NAME = GAME_CONFIG?.gameClaimName ?? null;

    // Useful dev state to mock the initial deposit screen
    const [canPlayGambitGame_DEV, setCanPlayGambitGame_DEV] = useState(false);
    const [isMasterGroupBankingInProgress, setIsMasterGroupBankingInProgress] = useState(false);
    const [showEndGameMessage, setShowEndGameMessage] = useState(false);
    const {unclaimedPromotions, unclaimedPromotionsLoaded} = _useUnclaimedPromotions();
    const [isAudioPlaying, setIsAudioPlaying] = useState(true);

    const toggleAudio = () => {
        setIsAudioPlaying(!isAudioPlaying);
    };

    const {_make, _update, playerClaimsLoaded, playerClaims} = useClaims();
    const {makePlayerClaim} = _make();
    const {updatePlayerClaim} = _update();
    const {findGameByCatalogueGameId} = useGames();

    const launchMasterGroupBanking = async () => {
        setIsMasterGroupBankingInProgress(true);
        captureEventForGambitGame('Launched banking');

        if (isTestMode) {
            await sleep(3000);
            setCanPlayGambitGame_DEV(true);
        }
    };

    const mastergroupBonus = useMemo(() => {
        if (!GAME_CONFIG) return null;
        if (!unclaimedPromotionsLoaded) return false;

        return unclaimedPromotions.find(promo => {
            try {
                const internalName = promo.internalName.toUpperCase();
                return internalName.startsWith(GAME_CONFIG.mastergroupBonus);
            } catch (err) {
                console.log(err);
            }
        })
    }, [GAME_CONFIG, unclaimedPromotions, unclaimedPromotionsLoaded]);

    const gameSkinId = useMemo(() => {
        if (!mastergroupBonus) return gambitGameConfigSkins?.[game];

        const KEY = "SkinId";

        try {
            if (mastergroupBonus?.customFields) {

                const skinId = mastergroupBonus.customFields.find(cf => cf?.key?.toLowerCase() === KEY.toLowerCase());
                if (skinId) {
                    // console.log("appliedTheme", appliedTheme);
                    // if (appliedTheme?.layout?.brandId !== "butlers-uk") {
                    //     return "WoollySafariWheel";
                    // }
                    return skinId?.value;
                }
            }
        } catch (e) {
            sendLogglyEventForGambitGame(KEY);
        }

        return gambitGameConfigSkins?.[game];
        // }, [mastergroupBonus, appliedTheme]);
    }, [mastergroupBonus]);

    const prizeBonus = useMemo(() => {
        if (!GAME_CONFIG) return null;
        if (!unclaimedPromotionsLoaded) return false;

        return unclaimedPromotions.find(p => p.internalName.toUpperCase().includes(GAME_CONFIG.prizeBonus));
    }, [GAME_CONFIG, unclaimedPromotions, unclaimedPromotionsLoaded])

    const setGamePlayedClaim = () => {
        if (isTestMode) return;
        if (!GAME_CONFIG.setGameClaimOnPlay) return;
        if (!playerClaimsLoaded) return;

        const existingClaim = playerClaims.find(claim => {
            return claim.type === GAMBIT_GAMES_CLAIM;
        });

        if (existingClaim) {
            try {
                let parsedClaimValue = JSON.parse(existingClaim.value);

                // Tidy up the claim. If a game has an old date, delete it.
                for (let gameClaimName in parsedClaimValue) {
                    if (gameClaimName?.lastPlayed !== TODAY)
                        delete parsedClaimValue[gameClaimName];
                }

                if (!parsedClaimValue?.[GAME_CLAIM_NAME]) {
                    parsedClaimValue[GAME_CLAIM_NAME] = {};
                }

                parsedClaimValue[GAME_CLAIM_NAME].lastPlayed = TODAY;

                updatePlayerClaim(existingClaim.id, JSON.stringify(parsedClaimValue));
            } catch (e) {
                console.log(e);
            }
        } else {
            let claimValue = {};
            claimValue[GAME_CLAIM_NAME] = {};
            claimValue[GAME_CLAIM_NAME].lastPlayed = TODAY;

            makePlayerClaim(GAMBIT_GAMES_CLAIM, JSON.stringify(claimValue));
        }
    };

    const canViewGame = useMemo(() => {
        if (!GAME_CONFIG || !GAME_CONFIG?.enabled) return null;
        if (!playerClaimsLoaded) return false;
        if (!mastergroupBonus || !prizeBonus) return false;

        const existingClaim = playerClaims.find(claim => {
            return claim.type === GAMBIT_GAMES_CLAIM;
        });

        if (existingClaim?.value) {
            const parsedClaimValue = JSON.parse(existingClaim.value);
            if (parsedClaimValue?.[GAME_CLAIM_NAME]?.lastPlayed === TODAY) return false;
        }

        return true;
    }, [GAME_CONFIG, mastergroupBonus, prizeBonus, playerClaims, playerClaimsLoaded]);

    const canPlayGame = useMemo(() => {
        if (!canViewGame) return false;

        return mastergroupBonus && !mastergroupBonus?.canBeClaimed && prizeBonus?.canBeClaimed;
    }, [GAME_CONFIG, canViewGame, mastergroupBonus, prizeBonus]);

    const winningGameDataFromCustomFields = useMemo(() => {
        if (!prizeBonus) return null;

        if (prizeBonus?.customFields) {
            const gameId = prizeBonus.customFields.find(cf => cf.key === "GameIdLaunchAfterClaim");
            if (gameId?.value) {
                return findGameByCatalogueGameId(gameId.value);
            }
        }

        return null;
    }, [prizeBonus]);

    const getVariantProperty = (prop) => {
        const defaultValue = GAME_CONFIG?.[prop] ?? null;

        if (!GAME_CONFIG?.variants) return defaultValue;
        if (!gameSkinId) return defaultValue;
        if (!GAME_CONFIG?.variants?.[gameSkinId]) return defaultValue;

        return GAME_CONFIG?.variants?.[gameSkinId]?.[prop] ?? defaultValue;
    }


    const audioEnabled = useMemo(() => {
        if (!mastergroupBonus) return null;
        const KEY = "audioEnabled";

        if (mastergroupBonus?.customFields) {
            const audio = mastergroupBonus.customFields.find(cf => cf?.key?.toLowerCase() === KEY.toLowerCase());
            if (audio) {
                return audio?.value.toLowerCase() === "true";
            } else {
                return getVariantProperty('audioEnabled');
            }
        }

        return null;
    }, [mastergroupBonus]);

    const videoBackgroundEnabled = useMemo(() => {
        if (!mastergroupBonus) return null;
        const KEY = "videoBgEnabled";

        if (mastergroupBonus?.customFields) {
            const videoBg = mastergroupBonus.customFields.find(cf => cf?.key?.toLowerCase() === KEY.toLowerCase());
            if (videoBg) {
                return videoBg?.value.toLowerCase() === "true";
            } else {
                return getVariantProperty('videoBackgroundEnabled');
            }
        }

        return null;
    }, [mastergroupBonus]);

    const captureEventForGambitGame = (eventMsg) => {
        try {
            const CATEGORY = "Gambit Game";
            const sharedData = {
                game_type: game,
                skin: gameSkinId,
            }

            sendLogglyEvent(CATEGORY, eventMsg, {
                ...sharedData
            });

            sendGA4Event(CATEGORY, {
                ...sharedData,
                event_msg: eventMsg,
            });
        }
        catch(e){
            console.error(e);
        }
    }

    const sendLogglyEventForGambitGame = (key) => {
        sendLogglyEvent(`Gambit${game}`, `Error parsing ${key} from Bede Bonus`);
    };


    const assetsPath = useMemo(() => setAssetsPathForGamification(gameSkinId), [gameSkinId]);
    const imagesPath = useMemo(() => setImagesPathForGamification(gameSkinId), [gameSkinId]);

    const gameConfig = {
        assetsPath,
        imagesPath,
        ...GAME_CONFIG,
        hasAudio: audioEnabled,
        videoBackgroundEnabled
    }

    return {
        isTestMode,
        isAudioPlaying,
        toggleAudio,
        gameConfig,
        setGamePlayedClaim,
        setCanPlayGambitGame_DEV,
        canPlayGambitGame: isTestMode ? canPlayGambitGame_DEV : canPlayGame,
        canViewGambitGame: canViewGame,
        gambitGamePrizeBonus: prizeBonus,
        gambitGameMastergroupBonus: mastergroupBonus,
        isMasterGroupBankingInProgress,
        launchMasterGroupBanking,
        showEndGameMessage,
        setShowEndGameMessage,
        winningGameDataFromCustomFields,
        sendLogglyEventForGambitGame,
        captureEventForGambitGame
    };
};